import urls from "@/constants/Urls";
import { showSnackbar } from "@/store/reducers/app";
import { clearAuthData } from "@/store/reducers/root";

import axios, { type AxiosError, type AxiosInstance, type AxiosResponse } from "axios";

import store from "../store";
import { errorHandler } from "./_helpers";
import { mobileSippeers } from "./mobileSippeers";

export { mobileSippeerSchema } from "./mobileSippeers";

export function api(): AxiosInstance {
  const instance: AxiosInstance = axios.create({
    baseURL: `${urls.api}/`,
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${store.getState().auth.jwtToken}`,
    },
  });

  instance.interceptors.response.use((response: AxiosResponse) => response, interceptor);

  return instance;
}

export function pbxApi(): AxiosInstance {
  const instance: AxiosInstance = axios.create({
    baseURL: `${urls.api}/${store.getState().domain.pbx?.context}/`,
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${store.getState().auth.jwtToken}`,
    },
  });

  instance.interceptors.response.use((response: AxiosResponse) => response, interceptor);

  return instance;
}

function interceptor(error: AxiosError) {
  if (error.response?.status === 401) {
    store.dispatch(clearAuthData());
    store.dispatch(showSnackbar("Your session has expired. Please log in again."));
  }

  return Promise.reject(error);
}

/* Backstage calls */
// PBXes
export async function getPbxes() {
  try {
    const response = await api().get(`pbxes`);
    return response.data.pbxes;
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching PBXes." });
  }
}

// Partners
export async function getPartners() {
  try {
    const response = await api().get(`partners`);
    return response.data.partners;
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching partners." });
  }
}

// Phone numbers
export async function getPhoneNumbers({
  page,
  number,
  pbx,
  partner,
  type,
}: {
  page: number;
  number: string;
  pbx: number | "assigned" | "unassigned" | undefined;
  partner: number | "assigned" | "unassigned" | undefined;
  type: undefined | "fixed" | "mobile";
}) {
  try {
    const queryState =
      partner === "unassigned" || pbx === "unassigned"
        ? "unassigned"
        : partner === "assigned" || pbx === "assigned"
          ? "assigned"
          : undefined;
    const queryPartner =
      typeof partner === "string" && ["assigned", "unassigned"].includes(partner) ? undefined : partner || undefined;
    const queryPbx = typeof pbx === "string" && ["assigned", "unassigned"].includes(pbx) ? undefined : pbx || undefined;

    const response = await api().get(`numbers`, {
      params: {
        limit: 50,
        page,
        searchDid: number || undefined,
        partner: queryPartner,
        pbx: queryPbx,
        state: queryState,
        type: type || undefined,
      },
    });
    return {
      total: response.data.count,
      phoneNumbers: response.data.phoneNumbers,
    };
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching the phone number list." });
  }
}

/* PBX calls */
// Phone numbers
export async function getCurrentPhoneNumbers() {
  try {
    const response = await pbxApi().get(`numbers`);
    return response.data.phoneNumbers;
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching phone numbers." });
  }
}

// Provisioning
export async function getProvisioningPhones(state = null) {
  try {
    const response = await pbxApi().get(`provisioning/phones`, { params: { state } });
    return response.data.phones;
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching phones." });
  }
}

// Queues
export async function getQueues() {
  try {
    const response = await pbxApi().get(`queues`);
    return response.data.queues;
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching queues." });
  }
}

export async function getPossibleQueueMembers() {
  try {
    const response = await pbxApi().get(`queues/members`);
    return response.data.data;
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching queue members." });
  }
}

// Sippers
export async function getSippeers() {
  try {
    const response = await pbxApi().get(`sippeers/all`);
    return response.data.sippeers;
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching sippeers." });
  }
}

// Inbound routes
export async function getInboundRoutes() {
  try {
    const response = await pbxApi().get(`inbound`);
    return response.data.inboundRoutes;
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching inbound routes." });
  }
}

// Webhooks
export async function getWebhooks() {
  try {
    const response = await pbxApi().get(`webhooks`);
    return response.data.webhooks;
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching webhooks." });
  }
}

// Logs

// Call logs
export async function getCallLog({
  page,
  order,
  orderBy,
  startDate,
  endDate,
  extension,
  from,
  to,
}: {
  page: number;
  order: "asc" | "desc";
  orderBy: "calldate" | "clid" | "src" | "dst" | "billsec";
  startDate: Date;
  endDate: Date;
  extension: string;
  from: string;
  to: string;
}) {
  try {
    const response = await pbxApi().get(`cdr`, {
      params: {
        limit: 50,
        page,
        order,
        orderby: orderBy,
        startdate: startDate,
        enddate: endDate,
        extension: extension || undefined,
        from: from || undefined,
        to: to || undefined,
      },
    });
    return {
      ...response.data,
      total: response.data.cdr.length,
      cdr: response.data.cdr.map((item: Record<string, unknown>) => {
        const id = Object.keys(item)[0];
        const data = item[id] as Record<string, unknown>;

        return {
          ...data,
          id,
        };
      }),
    };
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching the call log." });
  }
}

export async function getCallLogDownload({
  order,
  orderBy,
  startDate,
  endDate,
  extension,
  from,
  to,
}: {
  order: "asc" | "desc";
  orderBy: "calldate" | "clid" | "src" | "dst" | "billsec";
  startDate: Date;
  endDate: Date;
  extension: string;
  from: string;
  to: string;
}) {
  try {
    return await pbxApi().get(`cdr/download`, {
      params: {
        order,
        orderby: orderBy,
        startdate: startDate,
        enddate: endDate,
        extension: extension || undefined,
        from: from || undefined,
        to: to || undefined,
      },
    });
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching the call log." });
  }
}

// Flex desk log
export async function getFlexDeskLog({
  page,
  order,
  orderBy,
  startDate,
  endDate,
  mac,
  number,
}: {
  page: number;
  order: "asc" | "desc";
  orderBy: "date" | "device" | "name" | "event";
  startDate: Date;
  endDate: Date;
  mac: string;
  number: string;
}) {
  try {
    const response = await pbxApi().get(`fdl`, {
      params: {
        limit: 50,
        page,
        order,
        orderby: orderBy,
        startdate: startDate,
        enddate: endDate,
        mac: mac || undefined,
        number: number || undefined,
      },
    });
    return {
      ...response.data,
      total: response.data.fdl.length,
      fdl: response.data.fdl,
    };
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching the flex desk log." });
  }
}

// Queue logs
export async function getQueueLog({
  queues,
  agents,
  page,
  startDate,
  endDate,
  events,
  extensions,
  select,
  order,
  orderBy,
}: {
  page: number;
  order: "asc" | "desc";
  orderBy: "date" | "callid" | "queuename" | "agent" | "event";
  startDate: Date;
  endDate: Date;
  queues: string[];
  agents: string[];
  events: string[];
  extensions: string;
  select: "callid" | "event";
}) {
  try {
    const response = await pbxApi().get(`qdr`, {
      params: {
        limit: 50,
        page,
        order,
        orderby: orderBy,
        startdate: startDate,
        enddate: endDate,
        select,
        queues: queues.length >= 1 ? queues.join(",") : undefined,
        agents: agents.length >= 1 ? agents.join(",") : undefined,
        events: events.length >= 1 ? events.join(",") : undefined,
        extensions: extensions || undefined,
      },
    });
    return {
      ...response.data,
      total: response.data.qdr.length,
      qdr: response.data.qdr.map((item: Record<string, unknown>) => item[Object.keys(item)[0]]),
    };
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching queue log." });
  }
}

export async function getQueueStats({
  queues,
  startDate,
  endDate,
  extensions,
}: {
  startDate: Date;
  endDate: Date;
  queues: string[];
  extensions: string;
}) {
  try {
    const response = await pbxApi().get(`qdr/stats`, {
      params: {
        startdate: startDate,
        enddate: endDate,
        queues: queues?.length >= 1 ? queues.join(",") : undefined,
        extensions: extensions || undefined,
      },
    });
    return response.data;
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching the queue statistics." });
  }
}

export async function getQueueMemberLog({
  page,
  order,
  orderBy,
  startDate,
  endDate,
  queue,
  member,
}: {
  page: number;
  order: "asc" | "desc";
  orderBy: "date" | "queueName" | "memberName" | "event";
  startDate: Date;
  endDate: Date;
  queue: string;
  member: string;
}) {
  try {
    const response = await pbxApi().get(`qml`, {
      params: {
        limit: 50,
        page,
        order,
        orderby: orderBy,
        startdate: startDate,
        enddate: endDate,
        queue: queue || undefined,
        member: member || undefined,
      },
    });
    return {
      ...response.data,
      total: response.data.qml.length,
      qml: response.data.qml,
    };
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching the queue member log." });
  }
}

export async function getWebhookLog({
  page,
  order,
  orderBy,
  startDate,
  endDate,
  webhook,
}: {
  page: number;
  order: "asc" | "desc";
  orderBy: "date" | "name" | "did" | "eventType" | "responseData" | "url";
  startDate: Date;
  endDate: Date;
  webhook: string;
}) {
  try {
    const response = await pbxApi().get(`wel`, {
      params: {
        limit: 50,
        page,
        order,
        orderby: orderBy,
        startdate: startDate,
        enddate: endDate,
        webhook: webhook || undefined,
      },
    });
    return {
      ...response.data,
      total: response.data.wel.length,
      wel: response.data.wel,
    };
  } catch (error: unknown) {
    errorHandler({ error, message: "An error occurred while fetching the webhook log." });
  }
}

export const StackApi = {
  MobileSippeers: mobileSippeers,
};

export default StackApi;
